import { useState, useEffect, useRef, useContext } from "react";
import { ThemeCtx } from "./AppShell";
import { C, API } from "./utils";

const PAGE       = 20;
const MEDIA_BASE = "https://cip.medsphere.net/kmpattach/";
const mediaUrl   = p => p ? MEDIA_BASE + p : "";
// ─── Premium animated reaction system ────────────────────────────────────────
const REACTIONS = [
  {
    key:"👍", label:"Like",
    render:(active,color)=>(
      <svg width="22" height="22" viewBox="0 0 24 24" fill={active?color:"none"}
        stroke={active?color:"#64748B"} strokeWidth="2" strokeLinecap="round">
        <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/>
        <path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/>
      </svg>
    )
  },
  {
    key:"❤️", label:"Love",
    render:(active)=>(
      <svg width="22" height="22" viewBox="0 0 24 24"
        fill={active?"#EF4444":"none"} stroke="#EF4444" strokeWidth="2" strokeLinecap="round">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
      </svg>
    )
  },
  {
    key:"😄", label:"Haha",
    render:(active,color)=>(
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={active?"#F59E0B":"#64748B"} strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" fill={active?"#FEF3C7":"none"}/>
        <path d="M8 13s1.5 2.5 4 2.5 4-2.5 4-2.5"/>
        <line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="3"/>
        <line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="3"/>
      </svg>
    )
  },
  {
    key:"😮", label:"Wow",
    render:(active)=>(
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={active?"#8B5CF6":"#64748B"} strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" fill={active?"#EDE9FE":"none"}/>
        <circle cx="12" cy="14" r="2" fill={active?"#8B5CF6":"#64748B"} stroke="none"/>
        <line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="3"/>
        <line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="3"/>
      </svg>
    )
  },
  {
    key:"🔥", label:"Fire",
    render:(active)=>(
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={active?"#EF4444":"#64748B"} strokeWidth="2" strokeLinecap="round">
        <path d="M12 22c-4.97 0-9-3.58-9-8 0-3 1.5-5.5 4-7-.5 2 .5 4 2 5 0-3 2-5.5 4-7-.5 2.5.5 5 2.5 6.5C17 10 18 8.5 18 7c2 1.5 3 4 3 6 0 4.42-4.03 9-9 9z"
          fill={active?"#FED7AA":"none"}/>
      </svg>
    )
  },
  {
    key:"✅", label:"Done",
    render:(active,color)=>(
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={active?"#10B981":"#64748B"} strokeWidth="2.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" fill={active?"#D1FAE5":"none"}/>
        <polyline points="8 12 11 15 16 9"/>
      </svg>
    )
  },
  {
    key:"❓", label:"Question",
    render:(active)=>(
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={active?"#3B82F6":"#64748B"} strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" fill={active?"#DBEAFE":"none"}/>
        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
        <line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="3"/>
      </svg>
    )
  },
];
const EMOJI_LIST = REACTIONS.map(r=>r.key);

// ─── Animated reaction button ─────────────────────────────────────────────────
function ReactionBtn({ r, active, color, size=22, onClick }) {
  const [popped, setPopped] = useState(false);
  const handle = () => {
    setPopped(true);
    setTimeout(()=>setPopped(false), 300);
    onClick();
  };
  return(
    <button onClick={handle} title={r.label}
      style={{background:active?`${color}12`:"none",
        border:active?`1.5px solid ${color}44`:"1.5px solid transparent",
        cursor:"pointer",padding:"4px 5px",borderRadius:"9px",
        transform:popped?"scale(1.4)":active?"scale(1.1)":"scale(1)",
        transition:"transform 0.15s cubic-bezier(.34,1.56,.64,1), background 0.15s",
        display:"flex",alignItems:"center",justifyContent:"center"}}>
      {r.render(active, color)}
    </button>
  );
}

const parseReactions = r => {
  if (!r || typeof r !== "object") return {};
  const out = {};
  for (const [e, v] of Object.entries(r))
    out[e] = typeof v === "number" ? {count:v,members:[]} : {count:v.count||0,members:v.members||[]};
  return out;
};

function fmtDate(u) {
  if (!u) return "";
  const d=new Date(u*1000), now=new Date();
  if (d.toDateString()===now.toDateString()) return "Today";
  const y=new Date(now); y.setDate(y.getDate()-1);
  if (d.toDateString()===y.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-GB",{day:"numeric",month:"short"});
}

function Avatar({name,color,size=28}) {
  const ini=(name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  return <div style={{width:size,height:size,borderRadius:"50%",background:`${color}22`,
    display:"flex",alignItems:"center",justifyContent:"center",
    fontSize:size*0.36,fontWeight:"800",color,flexShrink:0,userSelect:"none"}}>{ini}</div>;
}

// ─── Members popup ────────────────────────────────────────────────────────────
function MembersPopup({title,members,onClose}) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.3)",zIndex:9999,
      display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:"14px",padding:"18px 20px",
        minWidth:"210px",maxWidth:"280px",boxShadow:"0 10px 40px rgba(0,0,0,0.18)"}}
        onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:"12px"}}>
          <span style={{fontSize:"13px",fontWeight:"700",color:"#111"}}>{title} · {members.length}</span>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
            fontSize:"18px",color:"#9CA3AF",lineHeight:1,padding:0}}>×</button>
        </div>
        {!members.length
          ? <div style={{fontSize:"12px",color:"#9CA3AF",textAlign:"center",padding:"8px 0"}}>No responses</div>
          : members.map((m,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:"8px",padding:"5px 0",
              borderBottom:i<members.length-1?"1px solid #F3F4F6":"none"}}>
              <div style={{width:"24px",height:"24px",borderRadius:"50%",background:"#E5E7EB",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:"10px",fontWeight:"700",color:"#555",flexShrink:0}}>
                {(m.NAME||"?")[0].toUpperCase()}
              </div>
              <span style={{fontSize:"12px",color:"#374151"}}>{m.NAME}</span>
            </div>
          ))
        }
      </div>
    </div>
  );
}

// ─── Reaction pills ───────────────────────────────────────────────────────────
function ReactionPills({commentId,reactions:rawR,myReaction,user,color,onReacted,align="left",autoOpen=false}) {
  const [open,setOpen]   = useState(autoOpen);
  const [popup,setPopup] = useState(null);
  const ref = useRef();
  const reactions = parseReactions(rawR);
  const pills     = Object.entries(reactions).filter(([,v])=>v.count>0);

  useEffect(()=>{
    if(!open) return;
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",h); return()=>document.removeEventListener("mousedown",h);
  },[open]);

  const react=async emoji=>{
    setOpen(false);
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID); fd.append("commentid",commentId); fd.append("reaction",emoji);
      await fetch(`${API}/react_comment`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      onReacted&&onReacted();
    }catch(e){}
  };

  return(
    <>
      {popup&&<MembersPopup title={popup.title} members={popup.members} onClose={()=>setPopup(null)}/>}
      <div style={{display:"flex",alignItems:"center",gap:"3px",flexWrap:"wrap",
        justifyContent:align==="right"?"flex-end":"flex-start"}} ref={ref}>
        {pills.map(([emoji,v])=>(
          <button key={emoji} onClick={()=>setPopup({title:emoji,members:v.members})}
            title={v.members.map(m=>m.NAME).join(", ")}
            style={{padding:"1px 6px",borderRadius:"10px",cursor:"pointer",
              border:`1px solid ${myReaction===emoji?color:"#ddd"}`,
              background:myReaction===emoji?`${color}18`:"rgba(255,255,255,0.9)",
              display:"flex",alignItems:"center",gap:"2px",lineHeight:1.6,fontSize:"10px",
              userSelect:"none",backdropFilter:"blur(4px)"}}>
            {REACTIONS.find(r=>r.key===emoji)?.render(myReaction===emoji,color)||emoji}
            <span style={{color:"#555",fontWeight:"600"}}>{v.count}</span>
          </button>
        ))}
        <div style={{position:"relative"}}>
          <button onClick={()=>setOpen(s=>!s)}
            style={{width:"20px",height:"20px",borderRadius:"6px",border:"1px solid #ddd",
              background:"rgba(255,255,255,0.9)",cursor:"pointer",color:"#9CA3AF",fontSize:"12px",
              display:"flex",alignItems:"center",justifyContent:"center",padding:0,lineHeight:1}}>+</button>
          {open&&(
            <div style={{position:"absolute",top:"calc(100% + 4px)",
              ...(align==="right"?{right:0}:{left:0}),
              background:"#fff",borderRadius:"12px",padding:"5px 7px",
              boxShadow:"0 4px 16px rgba(0,0,0,0.14)",border:"1px solid #E5E7EB",
              display:"flex",gap:"2px",zIndex:400,whiteSpace:"nowrap"}}>
              {REACTIONS.map(r=>(
                <ReactionBtn key={r.key} r={r} active={myReaction===r.key}
                  color={color} size={18} onClick={()=>react(r.key)}/>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Quoted message preview ───────────────────────────────────────────────────
function QuotedMsg({quoted,color,isMe}) {
  if(!quoted) return null;
  const qname=quoted.NAME||`${quoted.FIRSTNAME||""} ${quoted.LASTNAME||""}`.trim()||"User";
  const qtext=quoted.TYPE==="image"?"📷 Photo":quoted.TYPE==="video"?"🎥 Video":(quoted.COMMENTS||"");
  const scrollTo=()=>{
    const el=document.getElementById(`msg-${quoted.COMMENTID}`);
    if(!el) return;
    el.scrollIntoView({behavior:"smooth",block:"center"});
    // flash highlight
    el.style.transition="background 0.2s";
    el.style.background="rgba(0,0,0,0.08)";
    setTimeout(()=>{el.style.background="transparent";},1200);
  };
  return(
    <div onClick={scrollTo} style={{borderLeft:`3px solid ${isMe?"rgba(255,255,255,0.5)":color}`,
      background:isMe?"rgba(0,0,0,0.12)":"rgba(0,0,0,0.05)",
      padding:"4px 8px",borderRadius:"4px",marginBottom:"6px",cursor:"pointer",
      transition:"background 0.15s"}}
      onMouseEnter={e=>e.currentTarget.style.background=isMe?"rgba(0,0,0,0.18)":"rgba(0,0,0,0.09)"}
      onMouseLeave={e=>e.currentTarget.style.background=isMe?"rgba(0,0,0,0.12)":"rgba(0,0,0,0.05)"}>
      <div style={{fontSize:"10px",fontWeight:"700",color:isMe?"rgba(255,255,255,0.85)":color,
        marginBottom:"1px"}}>{qname}</div>
      <div style={{fontSize:"11px",color:isMe?"rgba(255,255,255,0.7)":"#6B7280",
        whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:"190px"}}>
        {qtext}
      </div>
    </div>
  );
}

// ─── Poll item ────────────────────────────────────────────────────────────────
function PollItem({poll,comment,user,color,onAnswer,isMe=false}) {
  const [submitting,setSubmitting]=useState(false);
  const [selected,setSelected]=useState([]);
  const [textAns,setTextAns]=useState("");
  const [popup,setPopup]=useState(null);
  const expired=comment.EXPIRED==="YES";
  const hasAnswered=poll.MCQ!==0
    ?(poll.RESULT||[]).some(r=>(r.MEMBERS||[]).some(m=>String(m.USERID)===String(user.USERID)))
    :(poll.RESULT||[]).some(r=>String(r.USERID)===String(user.USERID));
  const totalVotes=(poll.RESULT||[]).reduce((s,r)=>s+(r.STATS||0),0);

  const submit=async()=>{
    if(submitting||(poll.MCQ===0&&!textAns.trim())||(poll.MCQ!==0&&!selected.length)) return;
    setSubmitting(true);
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID); fd.append("documentid",comment.DOCUMENTID);
      fd.append("type","answer");
      fd.append("poll_answer",JSON.stringify(poll.MCQ===0
        ?{pollid:poll.POLLID,answer:textAns.trim(),mcq:0}
        :{pollid:poll.POLLID,answer:(poll.OPTIONS||[]).map((_,i)=>selected.includes(i)?"1":"0"),mcq:poll.MCQ}));
      const r=await fetch(`${API}/insert_doc_comments`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(String(res.STATUS)==="1"){setSelected([]);setTextAns("");onAnswer&&onAnswer();}
    }catch(e){}
    setSubmitting(false);
  };

  return(
    <div style={{width:"100%",minWidth:"180px",maxWidth:"240px"}}>
      {popup&&<MembersPopup title={popup.opt} members={popup.members} onClose={()=>setPopup(null)}/>}
      <div style={{fontSize:"11px",fontWeight:"700",marginBottom:"8px",
        display:"flex",alignItems:"center",gap:"5px",flexWrap:"wrap",
        wordBreak:"break-word",lineHeight:1.4}}>
        📊 {poll.POLL_QUESTION}
        {expired&&<span style={{fontSize:"9px",background:"rgba(239,68,68,0.15)",color:"#EF4444",
          padding:"1px 6px",borderRadius:"20px",flexShrink:0}}>Expired</span>}
      </div>
      {(hasAnswered||expired)&&poll.MCQ!==0&&(
        <div style={{display:"flex",flexDirection:"column",gap:"6px"}}>
          {(poll.OPTIONS||[]).map((opt,i)=>{
            const res=poll.RESULT?.[i]||{};
            const isMe2=(res.MEMBERS||[]).some(m=>String(m.USERID)===String(user.USERID));
            const pct=parseFloat(res.PERCENTAGE)||0;
            return(
              <div key={i} onClick={()=>setPopup({opt,members:res.MEMBERS||[]})} style={{cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:"11px",
                  marginBottom:"3px",fontWeight:isMe2?"700":"400",gap:"6px",alignItems:"baseline"}}>
                  <span style={{flex:1,wordBreak:"break-word",lineHeight:1.3}}>
                    {isMe2?"✓ ":""}{opt}
                  </span>
                  <span style={{flexShrink:0,fontSize:"10px",
                    opacity:0.85,fontWeight:600,whiteSpace:"nowrap"}}>
                    {res.STATS||0} · {res.PERCENTAGE||"0%"}
                  </span>
                </div>
                <div style={{height:"4px",borderRadius:"3px",
                  background:isMe?"rgba(255,255,255,0.25)":"rgba(0,0,0,0.1)",overflow:"hidden"}}>
                  <div style={{height:"100%",borderRadius:"3px",
                    background:isMe?"rgba(255,255,255,0.9)":color,
                    width:`${pct}%`,transition:"width 0.5s ease"}}/>
                </div>
              </div>
            );
          })}
          <div style={{fontSize:"9px",opacity:0.6,marginTop:"2px"}}>
            {totalVotes} response{totalVotes!==1?"s":""} · tap to see who voted
          </div>
        </div>
      )}
      {!hasAnswered&&!expired&&poll.MCQ!==0&&(
        <div style={{display:"flex",flexDirection:"column",gap:"5px"}}>
          {(poll.OPTIONS||[]).map((opt,i)=>(
            <label key={i} style={{display:"flex",alignItems:"center",gap:"8px",
              cursor:"pointer",fontSize:"12px",userSelect:"none"}}>
              <input type={poll.MCQ===1?"radio":"checkbox"}
                checked={selected.includes(i)}
                onChange={()=>poll.MCQ===1?setSelected([i]):setSelected(s=>s.includes(i)?s.filter(x=>x!==i):[...s,i])}
                style={{accentColor:"#fff",cursor:"pointer",width:"14px",height:"14px"}}/>
              {opt}
            </label>
          ))}
          <button onClick={submit} disabled={submitting||!selected.length}
            style={{padding:"5px 14px",border:"none",borderRadius:"8px",
              background:"rgba(255,255,255,0.25)",color:"inherit",fontSize:"11px",
              fontWeight:"700",cursor:"pointer",alignSelf:"flex-start",
              opacity:!selected.length?"0.5":"1"}}>
            {submitting?"…":"Submit"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── WhatsApp down-arrow menu (appears inside bubble on hover) ────────────────
function MessageActions({isMe,color,isAdmin,canDeleteOwn,onReply,onReact,onDelete,user,commentId}) {
  const canDel = isAdmin||canDeleteOwn;
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiRef = useRef();

  useEffect(()=>{
    if(!emojiOpen) return;
    const h=e=>{if(emojiRef.current&&!emojiRef.current.contains(e.target))setEmojiOpen(false);};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[emojiOpen]);

  const react=async emoji=>{
    setEmojiOpen(false);
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID);
      fd.append("commentid",commentId);
      fd.append("reaction",emoji);
      await fetch(`${API}/react_comment`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      onReact&&onReact();
    }catch(e){}
  };

  const Btn=({icon,label,fn,danger=false})=>{
    const [hov,setHov]=useState(false);
    return(
      <button onClick={fn} title={label}
        onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
        style={{background:hov?(danger?"#FEF2F2":`${color}12`):"none",
          border:"none",cursor:"pointer",borderRadius:"14px",
          padding:"4px 7px",display:"flex",alignItems:"center",
          gap:"3px",fontSize:"14px",transition:"background 0.12s",lineHeight:1}}>
        <span>{icon}</span>
        {hov&&<span style={{fontSize:"10px",fontWeight:"600",
          color:danger?"#EF4444":color,whiteSpace:"nowrap"}}>{label}</span>}
      </button>
    );
  };

  return(
    <div style={{display:"flex",alignItems:"center",gap:"1px",
      background:"#fff",borderRadius:"20px",
      boxShadow:"0 2px 10px rgba(0,0,0,0.14)",
      border:"1px solid #E5E7EB",padding:"3px 5px",position:"relative"}}>
      <Btn icon="↩️" label="Reply"  fn={onReply}/>
      {/* React — emoji picker opens up or down based on position */}
      <div ref={emojiRef} style={{position:"relative"}}>
        <Btn icon="😊" label="React" fn={()=>setEmojiOpen(s=>!s)}/>
        {emojiOpen&&(()=>{
          const rect    = emojiRef.current?.getBoundingClientRect();
          const parent  = emojiRef.current?.closest("[data-msglist]")?.getBoundingClientRect();
          const midpoint= parent ? parent.top + parent.height/2 : window.innerHeight/2;
          const openUp  = rect ? rect.top > midpoint : false;
          return(
            <div style={{position:"absolute",
              ...(openUp?{bottom:"calc(100% + 4px)"}:{top:"calc(100% + 4px)"}),
              ...(isMe?{right:0}:{left:0}),
              background:"#fff",borderRadius:"14px",
              boxShadow:"0 4px 20px rgba(0,0,0,0.16)",
              border:"1px solid #E5E7EB",
              padding:"6px 8px",display:"flex",gap:"1px",
              zIndex:600,whiteSpace:"nowrap"}}>
              {REACTIONS.map(r=>(
                <ReactionBtn key={r.key} r={r} active={false}
                  color={color} size={22} onClick={()=>{ react(r.key); setEmojiOpen(false); }}/>
              ))}
            </div>
          );
        })()}
      </div>
      {canDel&&<Btn icon="🗑" label="Delete" fn={onDelete} danger/>}
    </div>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────
function Message({comment:c,prevComment,user,color,isAdmin,onDelete,onReacted,onReply,replyingTo,setReplyingTo}) {
  const isMe    = String(c.USERID)===String(user.USERID);
  const name    = c.NAME||`${c.FIRSTNAME||""} ${c.LASTNAME||""}`.trim()||"User";
  const isReply = !!c.PARENT_COMMENTID;
  const prevSame= prevComment
    && String(prevComment.USERID)===String(c.USERID)
    && !c.PARENT_COMMENTID && !prevComment.PARENT_COMMENTID
    && Math.abs((c.UNIXTIME||0)-(prevComment.UNIXTIME||0))<120;
  const showDate= !prevComment
    || new Date((c.UNIXTIME||0)*1000).toDateString()!==new Date((prevComment.UNIXTIME||0)*1000).toDateString();
  const hasPoll = c.TYPE==="poll"&&Array.isArray(c.POLL)&&c.POLL.length>0;
  const isImg   = c.TYPE==="image", isVid=c.TYPE==="video";
  const mUrl    = (isImg||isVid)?mediaUrl(c.COMMENTS):null;
  const parsedR = parseReactions(c.REACTIONS);
  const hasReact= Object.values(parsedR).some(v=>v.count>0);
  const myReact = c.MY_REACTION || Object.entries(parsedR).find(([,v])=>(v.members||[]).some(m=>String(m.USERID)===String(user.USERID)))?.[0] || null;
  const [hovered,    setHovered]    = useState(false);
  const [showReact,  setShowReact]  = useState(false);
  const [lightboxSrc,setLightboxSrc]= useState(null);
  const leaveTimer   = useRef(null);
  const bubbleRef    = useRef(null);
  const [toolbarAbove,setToolbarAbove] = useState(true);
  const handleEnter = () => {
    clearTimeout(leaveTimer.current);
    setHovered(true);
    if(bubbleRef.current){
      const rect=bubbleRef.current.getBoundingClientRect();
      setToolbarAbove(rect.top > 120);
    }
  };
  const handleLeave = () => { leaveTimer.current = setTimeout(()=>setHovered(false), 600); };

  const bg = isMe ? color : "#fff";
  const fg = isMe ? "#fff" : "#111827";

  return(
    <>
      {showDate&&(
        <div style={{textAlign:"center",margin:"12px 0 8px"}}>
          <span style={{fontSize:"10px",color:"#555",background:"rgba(255,255,255,0.85)",
            padding:"3px 12px",borderRadius:"20px",boxShadow:"0 1px 3px rgba(0,0,0,0.08)",
            userSelect:"none"}}>{fmtDate(c.UNIXTIME)}</span>
        </div>
      )}

      {/* Hover row: arrow LEFT of bubble for isMe, RIGHT for others */}
      <div id={`msg-${c.COMMENTID}`} style={{display:"flex",flexDirection:"column",
        alignItems:isMe?"flex-end":"flex-start",
        padding:isMe?"1px 8px 28px 32px":"1px 32px 28px 8px",
        marginBottom:"2px"}}>

        <div ref={bubbleRef} style={{display:"flex",gap:"4px",alignItems:"flex-end",
          flexDirection:isMe?"row-reverse":"row",maxWidth:"86%"}}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}>

          {/* Avatar — others */}
          {!isMe&&(
            <div style={{flexShrink:0,alignSelf:"flex-end",marginBottom:"2px"}}>
              {(!prevSame||isReply)?<Avatar name={name} color={color} size={26}/>:<div style={{width:26}}/>}
            </div>
          )}

          {/* Bubble column with floating actions */}
          <div style={{display:"flex",flexDirection:"column",
            alignItems:isMe?"flex-end":"flex-start",minWidth:0,maxWidth:"100%",position:"relative"}}>
            {/* Floating action bar — above or below based on position */}
            {hovered&&(
              <div style={{
                position:"absolute",
                ...(toolbarAbove
                  ?{bottom:"calc(100% + 4px)"}
                  :{top:"calc(100% + 4px)"}),
                ...(isMe?{right:0}:{left:0}),
                zIndex:200,pointerEvents:"auto"}}>
                <MessageActions isMe={isMe} color={color} isAdmin={isAdmin}
                  canDeleteOwn={isMe}
                  onReply={()=>onReply(c)}
                  onReact={onReacted}
                  onDelete={()=>onDelete(c.COMMENTID)}
                  user={user} commentId={c.COMMENTID}/>
              </div>
            )}
            <div style={{background:bg,color:fg,
                borderRadius:isMe?"12px 4px 12px 12px":"4px 12px 12px 12px",
                padding:isImg||isVid?"0":hasPoll?"10px 12px":"8px 12px",
                width:hasPoll?"240px":undefined,
                maxWidth:"100%",boxSizing:"border-box",
                overflow:hasPoll?"visible":"hidden",
                boxShadow:"0 1px 2px rgba(0,0,0,0.1)"}}>

              {/* Name inside bubble — others only */}
              {!isMe&&(!prevSame||isReply)&&(
                <div style={{fontSize:"10px",fontWeight:"700",
                  color:color,marginBottom:"3px"}}>{name}</div>
              )}

              {/* Quoted */}
              {c.QUOTED&&<div style={{padding:isImg||isVid?"8px 10px 4px":"0 0 4px"}}>
                <QuotedMsg quoted={c.QUOTED} color={color} isMe={isMe}/>
              </div>}

              {/* Text */}
              {c.TYPE==="text"&&(
                <div style={{fontSize:"13px",lineHeight:1.5,wordBreak:"break-word"}}>{c.COMMENTS}</div>
              )}
              {isImg&&mUrl&&(
                <>
                  {lightboxSrc&&<Lightbox src={lightboxSrc} onClose={()=>setLightboxSrc(null)}/>}
                  <img src={mUrl} alt="" onClick={e=>{e.stopPropagation();setLightboxSrc(mUrl);}}
                    style={{display:"block",maxWidth:"220px",maxHeight:"180px",
                      objectFit:"cover",cursor:"zoom-in"}}
                    onError={e=>e.target.style.display="none"}/>
                </>
              )}
              {isVid&&mUrl&&(
                <video src={mUrl} controls
                  style={{display:"block",maxWidth:"220px",maxHeight:"160px"}}/>
              )}
              {hasPoll&&c.POLL.map(p=>(
                <PollItem key={p.POLLID} poll={p} comment={c} user={user} color={color} onAnswer={onReacted} isMe={isMe}/>
              ))}

              {/* Timestamp inside bubble bottom-right */}
              <div style={{fontSize:"9px",color:isMe?"rgba(255,255,255,0.7)":"#9CA3AF",
                textAlign:"right",marginTop:"3px",
                padding:isImg||isVid?"0 8px 6px":"0"}}>
                {c.TIMESTAMP}
              </div>
            </div>

            {/* Reaction pills below bubble */}
            {hasReact&&(
              <div style={{marginTop:"3px"}}>
                <ReactionPills commentId={c.COMMENTID} reactions={c.REACTIONS}
                  myReaction={myReact} user={user} color={color}
                  onReacted={onReacted}
                  align={isMe?"right":"left"}/>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}

// ─── Reply preview bar ────────────────────────────────────────────────────────
function ReplyBar({comment,color,onCancel}) {
  if(!comment) return null;
  const name=comment.NAME||`${comment.FIRSTNAME||""} ${comment.LASTNAME||""}`.trim()||"User";
  const preview=comment.TYPE==="image"?"📷 Photo":comment.TYPE==="video"?"🎥 Video":(comment.COMMENTS||"");
  return(
    <div style={{display:"flex",alignItems:"center",gap:"8px",padding:"8px 12px",
      background:"#F8FAFC",borderTop:"1px solid #E5E7EB",borderLeft:`3px solid ${color}`}}>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:"10px",fontWeight:"700",color,marginBottom:"1px"}}>{name}</div>
        <div style={{fontSize:"11px",color:"#6B7280",overflow:"hidden",
          textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{preview}</div>
      </div>
      <button onClick={onCancel} style={{background:"none",border:"none",cursor:"pointer",
        color:"#9CA3AF",fontSize:"18px",lineHeight:1,padding:0,flexShrink:0}}>×</button>
    </div>
  );
}

// ─── Poll builder ─────────────────────────────────────────────────────────────
function PollBuilder({color,onPost,onClose}) {
  const [pollQ,setPollQ]=useState("");
  const [pollType,setPollType]=useState(1);
  const [options,setOptions]=useState(["",""]);
  const [expiry,setExpiry]=useState("");
  const [privacy,setPrivacy]=useState(0);
  const valid=pollQ.trim()&&(pollType===0||options.filter(o=>o.trim()).length>=2);
  return(
    <div style={{position:"absolute",bottom:0,left:0,right:0,background:"#fff",
      borderRadius:"16px 16px 0 0",padding:"14px 14px 10px",zIndex:50,
      boxShadow:"0 -6px 24px rgba(0,0,0,0.1)",borderTop:"1px solid #E5E7EB"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px"}}>
        <span style={{fontSize:"13px",fontWeight:"700",color:"#111"}}>📊 New Poll</span>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
          color:"#9CA3AF",fontSize:"20px",lineHeight:1,padding:0}}>×</button>
      </div>
      <input value={pollQ} onChange={e=>setPollQ(e.target.value)} placeholder="Ask a question…"
        style={{width:"100%",padding:"8px 12px",border:"1px solid #E5E7EB",borderRadius:"8px",
          fontSize:"12px",outline:"none",fontFamily:"inherit",marginBottom:"8px",boxSizing:"border-box"}}/>
      <div style={{display:"flex",gap:"4px",marginBottom:"8px",flexWrap:"wrap",alignItems:"center"}}>
        {[{v:1,l:"Single"},{v:2,l:"Multi"},{v:0,l:"Open"}].map(t=>(
          <button key={t.v} onClick={()=>setPollType(t.v)}
            style={{padding:"3px 10px",borderRadius:"6px",border:"none",cursor:"pointer",
              fontSize:"10px",fontWeight:"600",
              background:pollType===t.v?color:"#F1F5F9",color:pollType===t.v?"#fff":"#6B7280"}}>
            {t.l}</button>
        ))}
        <button onClick={()=>setPrivacy(p=>p===0?1:0)}
          style={{marginLeft:"auto",padding:"3px 10px",borderRadius:"6px",cursor:"pointer",
            border:`1px solid ${privacy===1?"#6366F1":"#E5E7EB"}`,
            background:privacy===1?"#EEF2FF":"transparent",
            color:privacy===1?"#6366F1":"#6B7280",fontSize:"10px",fontWeight:"600"}}>
          {privacy===1?"🔒 Private":"🌐 Public"}</button>
      </div>
      {pollType!==0&&(
        <div style={{display:"flex",flexDirection:"column",gap:"4px",marginBottom:"8px"}}>
          {options.map((opt,i)=>(
            <div key={i} style={{display:"flex",gap:"4px"}}>
              <input value={opt} onChange={e=>setOptions(o=>{const n=[...o];n[i]=e.target.value;return n;})}
                placeholder={`Option ${i+1}`}
                style={{flex:1,padding:"6px 10px",border:"1px solid #E5E7EB",borderRadius:"7px",
                  fontSize:"11px",outline:"none",fontFamily:"inherit"}}/>
              {options.length>2&&<button onClick={()=>setOptions(o=>o.filter((_,j)=>j!==i))}
                style={{background:"none",border:"none",cursor:"pointer",color:"#9CA3AF",fontSize:"16px",padding:"0 4px"}}>×</button>}
            </div>
          ))}
          {options.length<5&&<button onClick={()=>setOptions(o=>[...o,""])}
            style={{fontSize:"10px",color,background:"none",border:"none",cursor:"pointer",fontWeight:"600",alignSelf:"flex-start"}}>
            + Add option</button>}
        </div>
      )}
      <div style={{display:"flex",gap:"4px",alignItems:"center",marginBottom:"10px"}}>
        <span style={{fontSize:"10px",color:"#9CA3AF"}}>Expires:</span>
        {["15m","1h","4h","24h"].map(e=>(
          <button key={e} onClick={()=>setExpiry(x=>x===e?"":e)}
            style={{padding:"2px 8px",borderRadius:"6px",border:"none",cursor:"pointer",
              fontSize:"10px",fontWeight:"600",
              background:expiry===e?color:"#F1F5F9",color:expiry===e?"#fff":"#6B7280"}}>{e}</button>
        ))}
      </div>
      <div style={{display:"flex",gap:"6px"}}>
        <button onClick={()=>{setPollQ("");setOptions(["",""]);setExpiry("");setPrivacy(0);setPollType(1);}}
          style={{flex:1,padding:"9px",border:"1px solid #E5E7EB",borderRadius:"9px",
            background:"#fff",color:"#6B7280",fontSize:"11px",fontWeight:"600",cursor:"pointer"}}>Clear</button>
        <button onClick={()=>onPost({pollQ,pollType,options:options.filter(o=>o.trim()),expiry,privacy})}
          disabled={!valid}
          style={{flex:2,padding:"9px",border:"none",borderRadius:"9px",background:color,
            color:"#fff",fontSize:"12px",fontWeight:"700",cursor:"pointer",opacity:!valid?"0.5":"1"}}>
          Post Poll</button>
      </div>
    </div>
  );
}

// ─── Plus menu ────────────────────────────────────────────────────────────────
function PlusMenu({color,onPoll,onPhoto,onVideo}) {
  const [open,setOpen]=useState(false);
  const ref=useRef();
  useEffect(()=>{
    if(!open) return;
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",h); return()=>document.removeEventListener("mousedown",h);
  },[open]);
  return(
    <div ref={ref} style={{position:"relative"}}>
      <button onClick={()=>setOpen(s=>!s)}
        style={{width:"36px",height:"36px",borderRadius:"50%",border:"none",
          background:"transparent",cursor:"pointer",fontSize:"24px",color:"#9CA3AF",
          display:"flex",alignItems:"center",justifyContent:"center",
          transition:"transform 0.2s",transform:open?"rotate(45deg)":"none"}}>+</button>
      {open&&(
        <div style={{position:"absolute",top:"44px",right:0,background:"#fff",
          borderRadius:"14px",padding:"5px",boxShadow:"0 2px 20px rgba(0,0,0,0.12)",
          border:"1px solid #E5E7EB",minWidth:"160px",zIndex:500}}>
          {[
            {icon:"📊",label:"Poll",  bg:"#EEF2FF",ic:"#6366F1",fn:()=>{setOpen(false);onPoll();}},
            {icon:"📷",label:"Photo", bg:"#ECFDF5",ic:"#059669",fn:()=>{setOpen(false);onPhoto();}},
            {icon:"🎬",label:"Video", bg:"#FEF2F2",ic:"#DC2626",fn:()=>{setOpen(false);onVideo();}},
          ].map(({icon,label,bg,ic,fn})=>(
            <button key={label} onClick={fn}
              style={{display:"flex",alignItems:"center",gap:"10px",width:"100%",
                padding:"8px 12px",border:"none",background:"none",cursor:"pointer",
                borderRadius:"10px",textAlign:"left"}}
              onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
              onMouseLeave={e=>e.currentTarget.style.background="none"}>
              <div style={{width:"34px",height:"34px",borderRadius:"8px",background:bg,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:"17px",flexShrink:0}}>
                {icon}
              </div>
              <span style={{fontSize:"12px",color:"#374151",fontWeight:"600"}}>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Image preview before send ────────────────────────────────────────────────
function ImagePreview({src,onClose,onSend,sending}) {
  return(
    <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.9)",zIndex:100,
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"14px"}}>
      <img src={src} alt="" style={{maxWidth:"88%",maxHeight:"55%",borderRadius:"10px",objectFit:"contain"}}/>
      <div style={{display:"flex",gap:"10px"}}>
        <button onClick={onClose} style={{padding:"8px 22px",borderRadius:"9px",
          border:"1px solid rgba(255,255,255,0.3)",background:"transparent",color:"#fff",
          fontSize:"12px",cursor:"pointer"}}>Cancel</button>
        <button onClick={onSend} disabled={sending}
          style={{padding:"8px 28px",borderRadius:"9px",border:"none",background:"#fff",
            color:"#111",fontSize:"12px",fontWeight:"700",cursor:"pointer",opacity:sending?"0.6":"1"}}>
          {sending?"Sending…":"Send"}
        </button>
      </div>
    </div>
  );
}

// ─── Input bar ────────────────────────────────────────────────────────────────
function InputBar({user,documentId,color,onSent,replyingTo,onCancelReply,onPoll,onPollPost}) {
  const [text,setText]=useState("");
  const [sending,setSending]=useState(false);
  const [imgFile,setImgFile]=useState(null);
  const [imgPrev,setImgPrev]=useState(null);
  const [showEmoji,setShowEmoji]=useState(false);
  const fileRef=useRef(), textRef=useRef(), emojiRef=useRef();
  const SMILEYS=["😀","😂","😍","🥰","😎","🤔","😢","😡","👏","🙏","🔥","💯","✅","❤️","🎉","👍","👎","🤝"];

  useEffect(()=>{textRef.current?.focus();},[replyingTo]);
  useEffect(()=>{
    if(!showEmoji) return;
    const h=e=>{if(emojiRef.current&&!emojiRef.current.contains(e.target))setShowEmoji(false);};
    document.addEventListener("mousedown",h); return()=>document.removeEventListener("mousedown",h);
  },[showEmoji]);

  const send=async(override=null,extra=null)=>{
    if(sending) return;
    setSending(true);
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID); fd.append("documentid",documentId);
      fd.append("privacy","0"); fd.append("time",Math.floor(Date.now()/1000));
      if(replyingTo) fd.append("parent_commentid",replyingTo.COMMENTID);

      if(extra?.type==="poll"){
        const {pollQ,pollType,options,expiry,privacy}=extra;
        fd.set("privacy",String(privacy));
        fd.append("type","poll"); fd.append("comment",pollQ);
        fd.append("poll",JSON.stringify({poll_question:pollQ,mcq:pollType,options:pollType===0?[]:options}));
        if(expiry){const map={"15m":15*60,"1h":3600,"4h":4*3600,"24h":24*3600};const s=map[expiry];
          if(s)fd.append("expiretime",new Date(Date.now()+s*1000).toISOString().slice(0,19));}
        onPollPost&&onPollPost();
      } else if(override){
        fd.append("type",override.type); fd.append("media",override.file);
      } else {
        if(!text.trim()){setSending(false);return;}
        fd.append("type","text"); fd.append("comment",text.trim());
      }
      const r=await fetch(`${API}/insert_doc_comments`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(String(res.STATUS)==="1"){
        setText(""); setImgFile(null); setImgPrev(null);
        if(textRef.current) textRef.current.style.height="20px";
        if(fileRef.current)fileRef.current.value="";
        onCancelReply&&onCancelReply();
        onSent(); textRef.current?.focus();
      }
    }catch(e){}
    setSending(false);
  };

  return(
    <div style={{flexShrink:0,background:"#CFD8DC",borderTop:"1px solid #B0BEC5",
      position:"relative",zIndex:10}}>
      {imgPrev&&<ImagePreview src={imgPrev} sending={sending}
        onClose={()=>{setImgFile(null);setImgPrev(null);if(fileRef.current)fileRef.current.value="";}}
        onSend={()=>send({type:"image",file:imgFile})}/>}

      {/* Reply preview */}
      {replyingTo&&<ReplyBar comment={replyingTo} color={color} onCancel={onCancelReply}/>}

      <div style={{display:"flex",alignItems:"flex-end",gap:"6px",padding:"6px 8px"}}>
        {/* Emoji */}
        <div style={{position:"relative"}} ref={emojiRef}>
          <button onClick={()=>setShowEmoji(s=>!s)}
            style={{width:"36px",height:"36px",borderRadius:"50%",border:"none",
              background:"transparent",cursor:"pointer",fontSize:"22px",
              display:"flex",alignItems:"center",justifyContent:"center",color:"#9CA3AF",flexShrink:0}}>
            😊
          </button>
          {showEmoji&&(
            <div style={{position:"absolute",top:"44px",left:0,background:"#fff",
              borderRadius:"12px",padding:"8px",boxShadow:"0 4px 20px rgba(0,0,0,0.12)",
              border:"1px solid #E5E7EB",display:"grid",gridTemplateColumns:"repeat(6,1fr)",
              gap:"2px",zIndex:300,width:"192px"}}>
              {SMILEYS.map(e=>(
                <button key={e} onClick={()=>{setText(t=>t+e);setShowEmoji(false);textRef.current?.focus();}}
                  style={{background:"none",border:"none",cursor:"pointer",fontSize:"18px",
                    padding:"3px",borderRadius:"6px",lineHeight:1}}
                  onMouseEnter={e=>e.currentTarget.style.background="#F3F4F6"}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>{e}</button>
              ))}
            </div>
          )}
        </div>

        {/* Message input */}
        <div style={{flex:1,background:"#fff",borderRadius:"22px",
          padding:"8px 14px",display:"flex",alignItems:"center",
          boxShadow:"0 1px 3px rgba(0,0,0,0.06)",minWidth:0}}>
          <textarea ref={textRef} value={text}
            onChange={e=>{
              setText(e.target.value);
              // auto-resize
              e.target.style.height="auto";
              e.target.style.height=Math.min(e.target.scrollHeight,120)+"px";
            }}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
            placeholder="Message" rows={1}
            style={{flex:1,border:"none",outline:"none",background:"transparent",
              fontSize:"13px",fontFamily:"inherit",resize:"none",lineHeight:1.5,
              height:"20px",maxHeight:"120px",overflowY:"auto",minWidth:0,
              transition:"height 0.1s ease"}}/>
        </div>

        {/* Plus / Send */}
        {/* + or Send depending on text */}
        <input ref={fileRef} type="file" style={{display:"none"}}
          accept="image/jpeg,image/png,image/gif,video/mp4,video/quicktime"
          onChange={e=>{
            const f=e.target.files?.[0]; if(!f) return;
            if(f.type.startsWith("image/")){setImgFile(f);setImgPrev(URL.createObjectURL(f));}
            else send({type:"video",file:f});
          }}/>
        {!text.trim()
          ?<PlusMenu color={color} onPoll={onPoll}
              onPhoto={()=>{fileRef.current.accept='image/jpeg,image/png,image/gif';fileRef.current?.click();}}
              onVideo={()=>{fileRef.current.accept='video/mp4,video/quicktime';fileRef.current?.click();}}/>
          :(
          <button onClick={()=>send()} disabled={sending}
            style={{width:"36px",height:"36px",borderRadius:"50%",border:"none",
              background:color,color:"#fff",fontSize:"16px",cursor:"pointer",flexShrink:0,
              display:"flex",alignItems:"center",justifyContent:"center",
              boxShadow:"0 2px 8px rgba(0,0,0,0.2)",transition:"transform 0.1s"}}
            onMouseDown={e=>e.currentTarget.style.transform="scale(0.93)"}
            onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}>
            {sending?<div style={{width:"12px",height:"12px",border:"2px solid rgba(255,255,255,0.4)",
              borderTopColor:"#fff",borderRadius:"50%",animation:"_sp 0.7s linear infinite"}}/>:"➤"}
            <style>{"@keyframes _sp{to{transform:rotate(360deg)}}"}</style>
          </button>)}
      </div>
    </div>
  );
}

// ─── Image Lightbox ──────────────────────────────────────────────────────────
function Lightbox({src,onClose}) {
  useEffect(()=>{
    const h=e=>{ if(e.key==="Escape") onClose(); };
    document.addEventListener("keydown",h);
    return()=>document.removeEventListener("keydown",h);
  },[]);
  return(
    <div onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:9998,
        display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out"}}>
      <button onClick={onClose}
        style={{position:"absolute",top:"16px",right:"20px",background:"rgba(255,255,255,0.15)",
          border:"none",borderRadius:"50%",width:"36px",height:"36px",cursor:"pointer",
          color:"#fff",fontSize:"20px",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
      <img src={src} alt="" onClick={e=>e.stopPropagation()}
        style={{maxWidth:"90vw",maxHeight:"88vh",borderRadius:"8px",objectFit:"contain",
          boxShadow:"0 8px 40px rgba(0,0,0,0.5)"}}/>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CommentsPanel({user,activeSub,documentId,pollOnly=false,scrollToComment=null}) {
  const ctx    = useContext(ThemeCtx)||{};
  const color  = ctx.theme?.primary||C.primary;
  const isAdmin= activeSub?.ROLE===1;
  const [comments,  setComments]   = useState([]);
  const [loading,   setLoading]    = useState(true);
  const [hasMore,   setHasMore]    = useState(false);
  const [offset,    setOffset]     = useState(0);
  const [replyingTo,setReplyingTo] = useState(null);
  const listRef      = useRef();
  const wsRef        = useRef(null);
  const scrollTopRef = useRef(0); // track scroll position for WS callbacks
  const [wsStatus,  setWsStatus]   = useState("connecting");
  const [wsRetry,   setWsRetry]    = useState(0);
  const [unreadNew, setUnreadNew]  = useState(0);

  const scrollToTop=()=>{
    if(listRef.current) listRef.current.scrollTop=0;
  };

  const scrollToBottom=()=>{
    if(listRef.current) listRef.current.scrollTop=listRef.current.scrollHeight;
  };

  const isNearTop=()=> scrollTopRef.current < 80;

  const load=async(reset=false, skipScroll=false)=>{
    if(!user||!activeSub||!documentId) return;
    setLoading(true);
    try{
      const o=reset?0:offset;
      const fd=new FormData();
      fd.append("userid",user.USERID); fd.append("subprojectid",activeSub.SUBPROJECTID);
      fd.append("documentid",documentId); fd.append("first",o); fd.append("last",PAGE);
      const r=await fetch(`${API}/common_get_chat_data`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if((res.STATUS==="1"||res.STATUS===1)&&Array.isArray(res.DATA)&&res.DATA.length>0){
        const raw=[...res.DATA];
        const data=pollOnly?raw.filter(c=>c.TYPE==="poll"):raw;
        setComments(prev=>reset?data:[...prev,...data]);
        setHasMore(res.DATA.length===PAGE); setOffset(o+PAGE);
        if(reset&&!skipScroll) requestAnimationFrame(()=>requestAnimationFrame(()=>scrollToTop()));
      }else{setHasMore(false);if(reset)setComments([]);}
    }catch(e){setHasMore(false);}
    setLoading(false);
  };

  useEffect(()=>{load(true);},[documentId]);

  // Scroll to specific comment after load
  useEffect(()=>{
    if(!scrollToComment||loading) return;
    const t = document.getElementById("msg-"+scrollToComment);
    if(t){
      t.scrollIntoView({behavior:"smooth",block:"center"});
      // Brief highlight flash
      t.style.transition="background 0.3s";
      t.style.background="#FEF3C7";
      setTimeout(()=>{ t.style.background=""; }, 2000);
    }
  },[loading, scrollToComment]);

  // WebSocket — real-time updates
  useEffect(()=>{
    if(!documentId) return;
    let ws = null;
    let reconnectTimer = null;
    let mounted = true;

    const applyEvent = (message) => {
      if(!message||!mounted) return;
      if(message.event==="new_message"&&message.comment){
        setComments(prev=>{
          if(prev.find(c=>c.COMMENTID===message.comment.COMMENTID)) return prev;
          return [message.comment, ...prev];
        });
        // if user is scrolled down — show unread badge instead of jumping
        if(!isNearTop()){
          setUnreadNew(n=>n+1);
        } else {
          scrollToTop();
        }
        load(true, true).then(()=>{
          window.dispatchEvent(new CustomEvent("pvision-refetch-count"));
        });
      }
      if(message.event==="new_reaction"&&message.commentid){
        setComments(prev=>prev.map(c=>{
          if(c.COMMENTID===message.commentid) return{...c,REACTIONS:message.reactions};
          return{...c,REPLIES:(c.REPLIES||[]).map(r=>
            r.COMMENTID===message.commentid?{...r,REACTIONS:message.reactions}:r)};
        }));
      }
      if(message.event==="delete_message"&&message.commentid){
        setComments(prev=>prev
          .map(c=>({...c,REPLIES:(c.REPLIES||[]).filter(r=>r.COMMENTID!==message.commentid)}))
          .filter(c=>c.COMMENTID!==message.commentid));
      }
    };

    const connect = () => {
      if(!mounted) return;
      ws = new WebSocket("wss://cip.medsphere.net:8082");
      wsRef.current = ws;
      setWsStatus("connecting");

      ws.onopen = () => {
        if(!mounted){ ws.close(); return; }
        setWsStatus("open");
        ws.send(JSON.stringify({ type:"register", uniqueid:`doc_${documentId}` }));
      };

      ws.onmessage = (e) => {
        if(!mounted) return;
        try {
          const { message } = JSON.parse(e.data);
          applyEvent(message);
        } catch(err){}
      };

      ws.onerror = () => { setWsStatus("error"); };

      ws.onclose = () => {
        wsRef.current = null;
        setWsStatus("closed");
        if(mounted) reconnectTimer = setTimeout(connect, 3000);
      };
    };

    const initTimer = setTimeout(connect, 50);

    return ()=>{
      mounted = false;
      clearTimeout(initTimer);
      clearTimeout(reconnectTimer);
      if(ws && ws.readyState!==WebSocket.CLOSED) ws.close();
      wsRef.current = null;
    };
  },[documentId]);

  const handleDelete=async commentId=>{
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID); fd.append("documentid",documentId);
      fd.append("commentid",commentId);
      await fetch(`${API}/delete_doccomment`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      setComments(c=>c.filter(x=>x.COMMENTID!==commentId));
    }catch(e){}
  };

  const [showPollCP,setShowPollCP]=useState(false);
  const handleSent=()=>{load(true);};

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",
      background:"#DFE7EC",position:"relative"}}>
      {/* subtle diagonal line pattern for depth */}
      <div style={{position:"absolute",inset:0,pointerEvents:"none",opacity:0.25,
        backgroundImage:`url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M-4 24L24-4M0 40L40 0M16 40L40 16' stroke='%23B0BEC5' stroke-width='1' fill='none'/%3E%3C/svg%3E")`,
        backgroundSize:"40px 40px"}}/>

      {/* WS status badge */}
      <div style={{position:"absolute",top:4,right:6,zIndex:50,
        padding:"2px 7px",borderRadius:"20px",fontSize:"9px",fontWeight:"700",
        background:wsStatus==="open"?"#D1FAE5":wsStatus==="error"?"#FEE2E2":wsStatus==="closed"?"#FEF3C7":"#E0E7FF",
        color:wsStatus==="open"?"#065F46":wsStatus==="error"?"#991B1B":wsStatus==="closed"?"#92400E":"#3730A3",
        boxShadow:"0 1px 4px rgba(0,0,0,0.1)"}}>
        {wsStatus==="open"?"● Live":wsStatus==="error"?"● WS Error":wsStatus==="closed"?"● Disconnected":"○ Connecting…"}
      </div>

      {/* Input at top */}
      {showPollCP&&(
        <div style={{position:"absolute",bottom:0,left:0,right:0,zIndex:400}}>
          <PollBuilder color={color} onClose={()=>setShowPollCP(false)}
            onPost={data=>{
              // trigger send via InputBar ref not available, so post directly
              const fd=new FormData();
              const {pollQ,pollType,options,expiry,privacy}=data;
              fd.append("userid",user.USERID); fd.append("documentid",documentId);
              fd.append("privacy",String(privacy)); fd.append("time",Math.floor(Date.now()/1000));
              fd.append("type","poll"); fd.append("comment",pollQ);
              fd.append("poll",JSON.stringify({poll_question:pollQ,mcq:pollType,options:pollType===0?[]:options}));
              if(expiry){const map={"15m":15*60,"1h":3600,"4h":4*3600,"24h":24*3600};const s=map[expiry];
                if(s)fd.append("expiretime",new Date(Date.now()+s*1000).toISOString().slice(0,19));}
              fetch(`${API}/insert_doc_comments`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd})
                .then(r=>r.json()).then(r=>{if(String(r.STATUS)==="1"){setShowPollCP(false);load(true);}});
            }}/>
        </div>
      )}
      {!pollOnly&&(
        <InputBar user={user} documentId={documentId} color={color} onSent={handleSent}
          replyingTo={replyingTo} onCancelReply={()=>setReplyingTo(null)}
          onPoll={()=>setShowPollCP(s=>!s)}
          onPollPost={()=>setShowPollCP(false)}/>
      )}

      {/* Unread new messages banner */}
      {unreadNew>0&&(
        <div onClick={()=>{ scrollToTop(); setUnreadNew(0); }}
          style={{position:"relative",zIndex:20,margin:"4px 12px 0",
            padding:"6px 14px",borderRadius:20,
            background:color,color:"#fff",
            fontSize:12,fontWeight:700,cursor:"pointer",
            display:"flex",alignItems:"center",justifyContent:"center",gap:6,
            boxShadow:"0 2px 8px rgba(0,0,0,0.2)",
            animation:"ntfSlide .25s ease"}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
          {unreadNew} new message{unreadNew!==1?"s":""}
          <style>{`@keyframes ntfSlide{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
        </div>
      )}

      {/* Messages below — latest first */}
      <div ref={listRef} data-msglist="1"
        onScroll={e=>{ scrollTopRef.current=e.currentTarget.scrollTop; if(isNearTop()) setUnreadNew(0); }}
        style={{flex:1,overflowY:"auto",overflowX:"hidden",
        padding:"6px 0 4px",position:"relative"}}>
        {loading&&<div style={{textAlign:"center",padding:"30px",color:"#777",fontSize:"12px"}}>Loading…</div>}
        {!loading&&comments.length===0&&(
          <div style={{textAlign:"center",padding:"40px 20px"}}>
            <div style={{fontSize:"40px",marginBottom:"8px",opacity:0.3}}>{pollOnly?"📊":"💬"}</div>
            <div style={{fontSize:"13px",color:"#888",fontWeight:"500"}}>
              {pollOnly?"No polls yet":"Say something!"}
            </div>
          </div>
        )}
        {comments.map((c,i)=>(
          <Message key={c.COMMENTID} comment={c} prevComment={comments[i-1]||null}
            user={user} color={color} isAdmin={isAdmin}
            onDelete={handleDelete} onReacted={handleSent}
            onReply={msg=>setReplyingTo(msg)}
            replyingTo={replyingTo} setReplyingTo={setReplyingTo}/>
        ))}
        {hasMore&&!loading&&(
          <div style={{textAlign:"center",margin:"8px 0"}}>
            <button onClick={()=>load(false)}
              style={{padding:"4px 16px",border:"none",borderRadius:"20px",
                background:"rgba(255,255,255,0.85)",color:"#374151",
                fontSize:"11px",cursor:"pointer",fontWeight:"600",
                boxShadow:"0 1px 4px rgba(0,0,0,0.1)"}}>
              ↓ Load older
            </button>
          </div>
        )}
      </div>
    </div>
  );
}